import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  useTheme,
  Fade,
  Zoom,
  alpha,
  Divider,
  Chip,
  Alert,
  Button,
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import AuthForm from '../auth/AuthForm';

const Login = () => {
  const theme = useTheme();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const message = location.state?.message;
    if (message) setSuccessMessage(message);
  }, [location.state?.message]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        pt: { xs: 12, md: 15 },
        pb: { xs: 4, md: 8 },
      }}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={800}>
          <Box>
            {/* Logo / Brand Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Zoom in={true} timeout={1000}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  <LoginIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Zoom>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  mb: 1,
                  color: theme.palette.primary.main,
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                Welcome Back
              </Typography>

              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: '1.1rem' }}>
                Sign in to continue your journey
              </Typography>
            </Box>

            {/* Auth Form (shared with the AuthModal) */}
            <Zoom in={true} timeout={1200}>
              <Paper elevation={8} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: `2px solid ${theme.palette.grey[200]}` }}>
                {successMessage && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    {successMessage}
                  </Alert>
                )}

                <AuthForm initialMode="login" showToggle={false} />

                <Divider sx={{ my: 3 }}>
                  <Chip label="OR" size="small" />
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Don't have an account?{' '}
                    <Button
                      component={Link}
                      to="/signup"
                      variant="text"
                      sx={{ color: theme.palette.primary.main, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
                    >
                      Sign Up Now
                    </Button>
                  </Typography>
                </Box>
              </Paper>
            </Zoom>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                By signing in, you agree to our Terms of Service and Privacy Policy
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Login;
